import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  Banknote,
  HeartPulse,
  Smartphone,
  Key,
  Baby,
  PawPrint,
  Shirt,
  Heart,
  Info,
  Sparkles,
} from "lucide-react";
import { lightTap } from "../lib/haptics";

/**
 * Emergency Escape Kit — trauma-informed persistent checklist.
 *
 * • 8 collapsible sections (Identity, Money, Health, Communication, Keys,
 *   Children, Pets, Clothing).
 * • Each item is a check-off row; checked-state persists to localStorage.
 * • Cream info card with safe-storage guidance.
 * • Two rotating calming reminders at the bottom (picks one per mount).
 *
 * Storage key: `ss_ee_kit_v1` → { [itemId: string]: true }
 */

const STORAGE_KEY = "ss_ee_kit_v1";

const SECTIONS = [
  {
    id: "identity",
    icon: FileText,
    title: "Identity & Documents",
    items: [
      "Driver's license or ID",
      "Passport",
      "Birth certificates",
      "Social Security cards",
      "Health insurance cards",
      "Copies of important legal documents",
    ],
  },
  {
    id: "money",
    icon: Banknote,
    title: "Money & Financial",
    items: [
      "Cash if possible",
      "Debit or credit card",
      "Bank account information",
      "List of important account numbers",
    ],
  },
  {
    id: "health",
    icon: HeartPulse,
    title: "Health",
    items: [
      "Prescription medications",
      "Glasses or contacts",
      "Medical devices",
      "Copies of prescriptions",
    ],
  },
  {
    id: "communication",
    icon: Smartphone,
    title: "Communication",
    items: [
      "Phone charger",
      "Portable battery pack",
      "Written list of important phone numbers",
      "Emergency contacts",
    ],
  },
  {
    id: "keys",
    icon: Key,
    title: "Keys & Access",
    items: [
      "House keys",
      "Car keys",
      "Work keys",
      "Storage unit keys if applicable",
    ],
  },
  {
    id: "children",
    icon: Baby,
    title: "Children",
    items: [
      "Favorite comfort item",
      "Diapers or formula if needed",
      "School records if appropriate",
      "Medications",
    ],
  },
  {
    id: "pets",
    icon: PawPrint,
    title: "Pets",
    items: ["Food", "Leash or carrier", "Vaccination records", "Medications"],
  },
  {
    id: "clothing",
    icon: Shirt,
    title: "Clothing & Personal Items",
    items: [
      "Change of clothes",
      "Underwear",
      "Toiletries",
      "Feminine hygiene products",
    ],
  },
];

const CALMING_REMINDERS = [
  "You do not have to do everything perfectly. Preparing even one item today is a meaningful step toward your safety.",
  "Leaving is often the most dangerous time in an abusive relationship. You do not have to do this alone. Every situation is different. Your safety comes first. If you believe you're in immediate danger, contact emergency services or a trusted domestic violence organization.",
];

function itemId(sectionId, index) {
  return `${sectionId}:${index}`;
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function ChecklistSection({ section, checked, onToggle, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = section.icon;
  const total = section.items.length;
  const done = section.items.filter((_, i) => checked[itemId(section.id, i)]).length;

  return (
    <section
      data-testid={`eek-section-${section.id}`}
      className="rounded-3xl bg-card border border-border overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`eek-section-toggle-${section.id}`}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-brand-soft/20 transition-colors min-h-[56px]"
      >
        <div className="w-9 h-9 rounded-xl bg-brand-soft/60 flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5 text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg leading-tight">{section.title}</p>
          <p className="text-xs text-foreground/55 mt-0.5">
            {done} of {total} gathered
          </p>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-foreground/45 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-foreground/45 shrink-0" />
        )}
      </button>

      <div
        className={`grid transition-all duration-400 ease-in-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <ul className="px-5 pb-5 pt-1 space-y-1">
            {section.items.map((label, i) => {
              const id = itemId(section.id, i);
              const isChecked = !!checked[id];
              return (
                <li key={id}>
                  <label
                    className="flex items-start gap-3 py-3 cursor-pointer rounded-2xl px-2 -mx-2 hover:bg-brand-soft/25 transition-colors min-h-[44px]"
                    data-testid={`eek-item-${id}`}
                  >
                    <span
                      className={`shrink-0 mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                        isChecked
                          ? "bg-brand border-brand"
                          : "bg-background border-border"
                      }`}
                      aria-hidden="true"
                    >
                      {isChecked && (
                        <svg
                          viewBox="0 0 20 20"
                          className="w-4 h-4 text-white"
                          fill="currentColor"
                        >
                          <path d="M7.5 14.5l-4-4 1.4-1.4 2.6 2.6 6.6-6.6 1.4 1.4z" />
                        </svg>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isChecked}
                      onChange={() => onToggle(id)}
                      data-testid={`eek-cb-${id}`}
                      aria-label={label}
                    />
                    <span
                      className={`text-[15px] leading-snug ${
                        isChecked ? "text-foreground/50 line-through" : "text-foreground/85"
                      }`}
                    >
                      {label}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

export default function EmergencyEscapeKit() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(loadState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
    } catch {
      /* localStorage full or blocked — silently continue */
    }
  }, [checked]);

  const onToggle = (id) => {
    lightTap();
    setChecked((c) => {
      const next = { ...c };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const reminder = useMemo(
    () => CALMING_REMINDERS[Math.floor(Math.random() * CALMING_REMINDERS.length)],
    []
  );

  const totalDone = Object.keys(checked).length;
  const totalItems = SECTIONS.reduce((n, s) => n + s.items.length, 0);

  return (
    <AppShell>
      <div
        className="px-6 sm:px-8 pt-14 pb-6"
        data-testid="page-emergency-escape-kit"
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-3 min-h-[44px]"
          data-testid="eek-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <p className="font-script text-2xl text-brand">preparation, gently</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">
          Emergency Escape Kit
        </h1>
        <p className="mt-4 text-foreground/75 leading-relaxed">
          If you may need to leave quickly, preparing a small emergency kit ahead of time can
          make leaving safer. You do not need to complete everything at once. Every small step
          helps.
        </p>
        <p
          className="mt-3 text-xs text-foreground/55"
          data-testid="eek-progress"
          aria-live="polite"
        >
          {totalDone} of {totalItems} gathered · saved on this device
        </p>
      </div>

      <div className="px-6 sm:px-8 space-y-3 pb-8">
        {SECTIONS.map((s, i) => (
          <ChecklistSection
            key={s.id}
            section={s}
            checked={checked}
            onToggle={onToggle}
            defaultOpen={i === 0}
          />
        ))}
      </div>

      {/* Safety notes — soft cream information card */}
      <div className="px-6 sm:px-8 pb-8">
        <div
          className="rounded-3xl border p-5 flex gap-3"
          style={{ backgroundColor: "#FBF3E5", borderColor: "#EBDDBF" }}
          data-testid="eek-safety-notes"
        >
          <Info className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <div className="text-sm text-foreground/85 leading-relaxed space-y-2">
            <p className="font-serif text-base text-foreground">Safety notes</p>
            <ul className="space-y-1.5 list-disc pl-4">
              <li>Store your bag somewhere it cannot easily be found if it is safe to do so.</li>
              <li>Consider leaving it with someone you trust if possible.</li>
              <li>Only gather items if it is safe. Your safety matters more than your belongings.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Rotating calming reminder */}
      <div className="px-6 sm:px-8 pb-24">
        <div
          className="rounded-3xl bg-brand/8 border border-brand/20 p-6 flex items-start gap-3"
          data-testid="eek-reminder"
        >
          {reminder === CALMING_REMINDERS[0] ? (
            <Sparkles className="w-5 h-5 text-brand mt-1 shrink-0" />
          ) : (
            <Heart className="w-5 h-5 text-brand mt-1 shrink-0" />
          )}
          <p className="font-serif italic text-foreground/85 leading-relaxed">{reminder}</p>
        </div>
      </div>
    </AppShell>
  );
}
